import { FinalishClient } from './client';
import type {
  FinalishTopLevelChildMessage,
  FinalishTopLevelOptions,
  FinalishTopLevelParentMessage,
} from './types';
import { FinalishWidget } from './widget';

const PROTOCOL_VERSION = 1;

export class FinalishTopLevelConnector {
  private readonly allowedReviewOrigins: Set<string>;
  private readonly defaultApiUrl?: string;
  private client?: FinalishClient;
  private widget?: FinalishWidget;
  private connectedOrigin?: string;
  private started = false;
  private connecting?: Promise<void>;
  private connectionRevision = 0;

  constructor(options: FinalishTopLevelOptions) {
    this.allowedReviewOrigins = new Set(
      options.allowedReviewOrigins.map(normalizeOrigin),
    );
    this.defaultApiUrl = options.defaultApiUrl
      ? normalizeHttpUrl(options.defaultApiUrl)
      : undefined;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener('message', this.handleMessage);
    this.postReady();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    window.removeEventListener('message', this.handleMessage);
    this.disconnect();
  }

  private disconnect(): void {
    this.connectionRevision += 1;
    this.widget?.destroy();
    this.widget = undefined;
    this.client = undefined;
    this.connectedOrigin = undefined;
  }

  private readonly handleMessage = (event: MessageEvent): void => {
    if (
      event.source !== window.opener ||
      !this.allowedReviewOrigins.has(normalizeOriginSafe(event.origin)) ||
      !isTopLevelParentMessage(event.data)
    ) {
      return;
    }
    if (event.data.type === 'disconnect-top-level') {
      this.disconnect();
      queueMicrotask(() => {
        if (this.started && !this.connectedOrigin) this.postReady();
      });
      return;
    }
    this.connectedOrigin = normalizeOriginSafe(event.origin);
    const revision = ++this.connectionRevision;
    const previous = this.connecting ?? Promise.resolve();
    this.connecting = previous
      .catch(() => undefined)
      .then(() => {
        if (!this.isCurrent(revision)) return;
        return this.connect(event.data, revision);
      })
      .catch((error) => {
        if (!this.isCurrent(revision)) return;
        this.post({
          source: 'finalish-sdk',
          type: 'top-level-error',
          message: errorMessage(error),
        });
      });
  };

  private async connect(
    message: Extract<FinalishTopLevelParentMessage, { type: 'init-top-level' }>,
    revision: number,
  ): Promise<void> {
    const apiUrl = normalizeHttpUrl(message.apiUrl || this.defaultApiUrl || '');
    if (this.client && this.widget) {
      this.client.setSessionToken(message.sessionToken, 'review');
      await this.widget.resume();
      if (!this.isCurrent(revision)) return;
    } else {
      const client = new FinalishClient({
        apiUrl,
        sessionToken: message.sessionToken,
        sessionKind: 'review',
      });
      const widget = new FinalishWidget({
        client,
        startHidden: true,
        onSessionExpired: () => {
          this.post({
            source: 'finalish-sdk',
            type: 'review-session-expired',
          });
        },
        onRouteChange: ({ href, title }) => {
          this.post({
            source: 'finalish-sdk',
            type: 'route-changed',
            href,
            title,
          });
        },
      });
      this.client = client;
      this.widget = widget;
      try {
        await widget.mount();
      } catch (error) {
        widget.destroy();
        if (this.widget === widget) this.widget = undefined;
        if (this.client === client) this.client = undefined;
        throw error;
      }
      if (!this.isCurrent(revision)) {
        widget.destroy();
        if (this.widget === widget) this.widget = undefined;
        if (this.client === client) this.client = undefined;
        return;
      }
      widget.show();
    }
    if (!this.isCurrent(revision)) return;
    this.post({
      source: 'finalish-sdk',
      type: 'top-level-connected',
      version: PROTOCOL_VERSION,
      href: location.href,
      title: document.title,
    });
  }

  private isCurrent(revision: number): boolean {
    return this.started && revision === this.connectionRevision;
  }

  private postReady(): void {
    if (!window.opener) return;
    const message: FinalishTopLevelChildMessage = {
      source: 'finalish-sdk',
      type: 'top-level-ready',
      version: PROTOCOL_VERSION,
      href: location.href,
      title: document.title,
    };
    for (const origin of this.allowedReviewOrigins) {
      window.opener.postMessage(message, origin);
    }
  }

  private post(message: FinalishTopLevelChildMessage): void {
    if (!window.opener || !this.connectedOrigin) return;
    window.opener.postMessage(message, this.connectedOrigin);
  }
}

function isTopLevelParentMessage(
  value: unknown,
): value is FinalishTopLevelParentMessage {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('source' in value) ||
    value.source !== 'finalish-review' ||
    !('type' in value)
  ) {
    return false;
  }
  if (value.type === 'disconnect-top-level') return true;
  return (
    value.type === 'init-top-level' &&
    'version' in value &&
    value.version === PROTOCOL_VERSION &&
    'apiUrl' in value &&
    typeof value.apiUrl === 'string' &&
    'sessionToken' in value &&
    typeof value.sessionToken === 'string' &&
    value.sessionToken.length > 0 &&
    'expiresAt' in value &&
    typeof value.expiresAt === 'string'
  );
}

function normalizeOrigin(value: string): string {
  return new URL(value).origin;
}

function normalizeOriginSafe(value: string): string {
  try {
    return normalizeOrigin(value);
  } catch {
    return '';
  }
}

function normalizeHttpUrl(value: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error('Finalish API URL must use HTTP or HTTPS');
  }
  return url.toString().replace(/\/+$/, '');
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Не удалось подключить прямое ревью.';
}
